import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown, AlertTriangle } from "lucide-react";
import { track } from "@/lib/track";
import { buildProposalHTML } from "@/lib/proposalHtml";

const START_POINTS = {
  ar: ["من تاريخ توقيع العرض", "من استلام الدفعة الأولى", "من استلام كامل المواد من العميل"],
  en: ["From the proposal signing date", "From receipt of the first payment", "From receipt of all client materials"],
};
const PAY_TERMS = {
  ar: ["٥٠٪ مقدمًا و٥٠٪ عند التسليم", "١٠٠٪ مقدمًا", "كامل المبلغ عند التسليم"],
  en: ["50% upfront, 50% on delivery", "100% upfront", "Full amount on delivery"],
};
const DEFAULT_TERMS = {
  ar: "أي عمل خارج نطاق العمل والتسليمات المذكورة يُسعَّر بشكل منفصل بعد الاتفاق.\nتُسلَّم الملفات النهائية بعد استكمال الدفعات المتفق عليها.",
  en: "Any work outside the stated scope and deliverables is priced separately upon agreement.\nFinal files are delivered once the agreed payments are complete.",
};

const iso = (d) => d.toISOString().slice(0, 10);
const loadFreelancer = () => { try { return JSON.parse(localStorage.getItem("pl_freelancer") || "{}"); } catch { return {}; } };

function Field({ label, error, children, hint, dark, testId }) {
  return (
    <label className="block">
      <span className={`block text-[11px] font-bold mb-1 ${dark ? "text-white/80" : "text-[#2d2c35]/80"}`}>{label}</span>
      {children}
      {hint && <span className={`block mt-0.5 text-[10.5px] ${dark ? "text-white/50" : "text-[#2d2c35]/55"}`}>{hint}</span>}
      {error && <span data-testid={testId ? `proposal-error-${testId}` : undefined} role="alert" className="block mt-0.5 text-[10.5px] font-bold text-[#f97316]">{error}</span>}
    </label>
  );
}

export default function ProposalModal({
  open, onOpenChange, lang = "ar", theme = "dark",
  serviceLabel, experienceLabel, complexityLabel, unitLabel, quantity, revisions, rush,
  description, price = 0, rushFee = 0, currencySymbol = "ر.س", scopeLines = [], priceSource = "estimate",
}) {
  const ar = lang === "ar";
  const dark = theme === "dark";
  const L = (a, e) => (ar ? a : e);
  const [f, setF] = useState({});
  const [errors, setErrors] = useState({});
  const quoteNo = useRef("");
  const inputCls = `w-full rounded-md border px-2.5 py-2 text-[13px] ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white placeholder:text-white/35" : "bg-white border-[#e5e5ea]"}`;

  useEffect(() => {
    if (!open) return;
    const saved = loadFreelancer();
    quoteNo.current = "PL-" + Date.now().toString(36).toUpperCase().slice(-6);
    setErrors({});
    setF({
      freelancerName: saved.name || "", freelancerContact: saved.contact || "",
      clientName: "", clientContact: "",
      projectTitle: "", serviceName: serviceLabel || "",
      scope: description || "", deliverables: scopeLines.join("\n"), exclusions: "",
      deliveryDays: 7, startPoint: 0, revisionsCount: revisions ?? 2,
      revisionsScope: L("تعديلات على نفس المطلوب ضمن نطاق العمل المتفق عليه", "Changes to the same requested work within the agreed scope"),
      price: Math.round(price || 0), payTerm: 0, payCustom: "",
      issueDate: iso(new Date()), validityDays: 14, terms: DEFAULT_TERMS[lang] || DEFAULT_TERMS.ar,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const onMsg = (e) => { if (e?.origin === window.location.origin && e?.data?.pl === "pdf_download") track("pdf_download", null, { once: false }); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const validUntil = useMemo(() => {
    if (!f.issueDate) return null;
    const d = new Date(f.issueDate + "T12:00:00");
    d.setDate(d.getDate() + Number(f.validityDays || 14));
    return d;
  }, [f.issueDate, f.validityDays]);
  const fmtDate = (d) => d?.toLocaleDateString(ar ? "ar-EG-u-ca-gregory" : "en-GB", { year: "numeric", month: "long", day: "2-digit" });

  const generate = () => {
    const err = {};
    if (!f.freelancerName?.trim()) err.freelancerName = L("اكتب اسمك أو اسم نشاطك", "Enter your name or business name");
    if (!f.projectTitle?.trim()) err.projectTitle = L("اكتب عنوانًا للمشروع", "Enter a project title");
    if (!f.serviceName?.trim()) err.serviceName = L("اكتب اسم الخدمة", "Enter the service name");
    if (!(Number(f.price) > 0)) err.price = L("أدخل سعرًا أكبر من صفر", "Enter a price above zero");
    if (!(Number(f.deliveryDays) > 0)) err.deliveryDays = L("أدخل مدة صحيحة", "Enter a valid duration");
    setErrors(err);
    if (Object.keys(err).length) return;
    try { localStorage.setItem("pl_freelancer", JSON.stringify({ name: f.freelancerName, contact: f.freelancerContact })); } catch {}
    const total = Math.round(Number(f.price));
    const html = buildProposalHTML({
      lang,
      data: {
        ...f,
        quoteNo: quoteNo.current,
        issueStr: fmtDate(new Date(f.issueDate + "T12:00:00")),
        validStr: fmtDate(validUntil),
        startPointStr: START_POINTS[lang][f.startPoint],
        payTermStr: f.payTerm === 3 ? f.payCustom : PAY_TERMS[lang][f.payTerm],
        total, rushFee: rush ? Math.min(Math.round(rushFee || 0), total) : 0, rush,
        currencySymbol, experienceLabel, complexityLabel, unitLabel, quantity,
      },
    });
    const w = window.open("", "_blank");
    if (!w) {
      setErrors({ popup: L("متصفحك منع فتح نافذة العرض. اسمح بالنوافذ المنبثقة ثم حاول مجددًا.", "Your browser blocked the proposal window. Allow pop-ups and try again.") });
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    track("pdf_generated", { from: priceSource }, { once: false });
  };

  const sel = (k, opts) => (
    <select data-testid={`proposal-${k}`} value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: Number(e.target.value) }))} className={inputCls}>
      {opts.map((o, i) => <option key={i} value={i}>{o}</option>)}
    </select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="proposal-modal" dir={ar ? "rtl" : "ltr"}
        className={`max-w-2xl max-h-[92vh] overflow-y-auto ${dark ? "bg-[#1b2540] border-[#2e3a5c] text-white" : "bg-white"} ${ar ? "font-arabic" : "font-outfit"}`}>
        <DialogHeader>
          <DialogTitle className="text-lg font-black flex items-center gap-2"><FileDown className="h-5 w-5 text-[#18c98f]" />{L("مراجعة عرض السعر", "Review your proposal")}</DialogTitle>
          <DialogDescription className={dark ? "text-white/65" : "text-[#2d2c35]/70"}>
            {L("راجع كل البنود وعدّلها قبل التحميل. التحميل مجاني ولا يحتاج تسجيلًا.", "Review and edit every item before downloading. Free, no sign-up.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-start">
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field dark={dark} label={L("اسمك / اسم نشاطك *", "Your name / business *")} error={errors.freelancerName} testId="freelancer-name">
              <input data-testid="proposal-freelancer-name" className={inputCls} value={f.freelancerName || ""} onChange={set("freelancerName")} />
            </Field>
            <Field dark={dark} label={L("وسيلة تواصلك", "Your contact")}>
              <input data-testid="proposal-freelancer-contact" className={inputCls} value={f.freelancerContact || ""} onChange={set("freelancerContact")} placeholder="05xxxxxxxx / email" dir="ltr" />
            </Field>
            <Field dark={dark} label={L("اسم العميل (اختياري)", "Client name (optional)")}>
              <input data-testid="proposal-client-name" className={inputCls} value={f.clientName || ""} onChange={set("clientName")} />
            </Field>
            <Field dark={dark} label={L("تواصل العميل (اختياري)", "Client contact (optional)")}>
              <input data-testid="proposal-client-contact" className={inputCls} value={f.clientContact || ""} onChange={set("clientContact")} dir="ltr" />
            </Field>
          </fieldset>

          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field dark={dark} label={L("عنوان المشروع *", "Project title *")} error={errors.projectTitle} testId="project-title">
              <input data-testid="proposal-project-title" className={inputCls} value={f.projectTitle || ""} onChange={set("projectTitle")} placeholder={L("مثال: هوية بصرية لمقهى", "e.g. Café brand identity")} />
            </Field>
            <Field dark={dark} label={L("اسم الخدمة كما يظهر في العرض *", "Service name on the proposal *")} error={errors.serviceName} testId="service-name"
              hint={L("تأكد أن الخدمة تطابق عنوان المشروع — عدّلها إن لزم.", "Make sure the service matches the project title — edit if needed.")}>
              <input data-testid="proposal-service-name" className={inputCls} value={f.serviceName || ""} onChange={set("serviceName")} />
            </Field>
          </fieldset>

          <Field dark={dark} label={L("نطاق العمل", "Scope of work")}>
            <textarea data-testid="proposal-scope" rows={3} className={inputCls} value={f.scope || ""} onChange={set("scope")} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field dark={dark} label={L("التسليمات (سطر لكل بند)", "Deliverables (one per line)")}>
              <textarea data-testid="proposal-deliverables" rows={3} className={inputCls} value={f.deliverables || ""} onChange={set("deliverables")} />
            </Field>
            <Field dark={dark} label={L("المستثنيات (اختياري)", "Exclusions (optional)")}>
              <textarea data-testid="proposal-exclusions" rows={3} className={inputCls} value={f.exclusions || ""} onChange={set("exclusions")} placeholder={L("مثال: شراء الصور والخطوط المدفوعة", "e.g. Paid stock photos and fonts")} />
            </Field>
          </div>

          <fieldset className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field dark={dark} label={L("مدة التنفيذ (أيام) *", "Duration (days) *")} error={errors.deliveryDays} testId="delivery-days">
              <input data-testid="proposal-delivery-days" type="number" min="1" className={inputCls} value={f.deliveryDays ?? ""} onChange={set("deliveryDays")} />
            </Field>
            <div className="col-span-1 sm:col-span-3">
              <Field dark={dark} label={L("تبدأ المدة", "Duration starts")}>{sel("startPoint", START_POINTS[lang])}</Field>
            </div>
            <Field dark={dark} label={L("جولات التعديل", "Revision rounds")}>
              <input data-testid="proposal-revisions" type="number" min="0" className={inputCls} value={f.revisionsCount ?? ""} onChange={set("revisionsCount")} />
            </Field>
            <div className="col-span-1 sm:col-span-3">
              <Field dark={dark} label={L("ما تشمله التعديلات", "What revisions cover")}>
                <input data-testid="proposal-revisions-scope" className={inputCls} value={f.revisionsScope || ""} onChange={set("revisionsScope")} />
              </Field>
            </div>
          </fieldset>

          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field dark={dark} label={L(`السعر الإجمالي (${currencySymbol}) *`, `Total price (${currencySymbol}) *`)} error={errors.price} testId="price"
              hint={priceSource === "ai" ? L("مأخوذ من اقتراح AI", "From the AI suggestion") : L("مأخوذ من التقدير الإرشادي", "From the estimate")}>
              <input data-testid="proposal-price" type="number" min="0" className={`${inputCls} font-mono-num`} value={f.price ?? ""} onChange={set("price")} dir="ltr" />
            </Field>
            <Field dark={dark} label={L("شروط الدفع", "Payment terms")}>
              {sel("payTerm", [...PAY_TERMS[lang], L("صيغة أخرى...", "Custom...")])}
              {f.payTerm === 3 && <input data-testid="proposal-pay-custom" className={`${inputCls} mt-1.5`} value={f.payCustom || ""} onChange={set("payCustom")} />}
            </Field>
            <Field dark={dark} label={L("تاريخ الإصدار", "Issue date")}>
              <input data-testid="proposal-issue-date" type="date" className={inputCls} value={f.issueDate || ""} onChange={set("issueDate")} />
            </Field>
            <Field dark={dark} label={L("صلاحية العرض", "Valid for")} hint={validUntil ? L(`صالح حتى ${fmtDate(validUntil)}`, `Valid until ${fmtDate(validUntil)}`) : null}>
              <select data-testid="proposal-validity" value={f.validityDays} onChange={(e) => setF((s) => ({ ...s, validityDays: Number(e.target.value) }))} className={inputCls}>
                {[7, 14, 30].map((d) => <option key={d} value={d}>{L(`${d} يومًا`, `${d} days`)}</option>)}
              </select>
            </Field>
          </fieldset>

          <Field dark={dark} label={L("الشروط (راجعها وعدّلها — تظهر كما هي)", "Terms (review & edit — shown as written)")}>
            <textarea data-testid="proposal-terms" rows={3} className={inputCls} value={f.terms || ""} onChange={set("terms")} />
          </Field>

          {errors.popup && (
            <div role="alert" className="flex items-start gap-1.5 text-[12px] text-[#f97316]"><AlertTriangle className="h-4 w-4 shrink-0" />{errors.popup}</div>
          )}
          <Button data-testid="proposal-generate" onClick={generate}
            className="w-full h-12 rounded-full bg-[#18c98f] text-white font-black text-[15px] hover:brightness-110 gap-2 shadow-[0_4px_0_0_#0e8a62]">
            <FileDown className="h-5 w-5" />{L("إنشاء عرض السعر", "Create proposal")}
          </Button>
          <p className={`text-center text-[10.5px] ${dark ? "text-white/50" : "text-[#2d2c35]/55"}`}>
            {L("يفتح العرض في نافذة جديدة. راجعه ثم اضغط «حفظ PDF / طباعة» واختر «حفظ كـ PDF».", "Opens in a new window — review it, tap “Save PDF / Print”, then choose “Save as PDF”.")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
